import React, { useState } from 'react';
import sanitizeHtml from 'sanitize-html';
import HorizontalBrightnessBlurSliders from 'react-scale/components/scalers/tasks/textcollection/HorizontalBrightnessBlurSliders';
import cx from 'classnames';
// @ts-ignore
import DefaultMarkdown from '@scale/frontend-lib/markdown';
import BulbaMarkdown from '@scale/frontend-lib/bulbaMarkdown';
import { makeStyles } from 'design-system/product/theme';

const useStyles = makeStyles({
  imageOverride: {
    '& img': {
      background: '#eee',
    },
  },
  inlineBlockCode: {
    '& code': {
      display: 'inline-block',
      whiteSpace: 'pre-wrap',
      maxWidth: '100%',
    },
  },
});

interface ISanitizedMarkdownProps {
  text: string;
  disableLinksInTextAttachment?: boolean;
  addBackgroundToTransparentImages?: boolean;
  handleLinkClickHistory?: (link: string) => void;
  addBrightnessSlider?: boolean;
  addBlurSlider?: boolean;
  disableFuzzyLinks?: boolean;
  escapeHtml?: boolean;
  innerStyle?: string | any;
}

type MarkdownSection = {
  index: number;
  content: string;
};

export const MARKDOWN_DIVIDER = '<!-- Section -->';
const MARKDOWN_DIVIDER_REGEX = /<!--\s*Section\s*-->/;

function parseMarkdownSections(markdownText: string): MarkdownSection[] {
  const sections: MarkdownSection[] = [];
  const splitMarkdown = markdownText.split(MARKDOWN_DIVIDER_REGEX);

  for (let i = 1; i < splitMarkdown.length; i++) {
    const content = splitMarkdown[i].trim();

    sections.push({
      index: i,
      content,
    });
  }

  return sections;
}

const SanitizedMarkdown: React.FC<ISanitizedMarkdownProps> = ({
  text,
  disableLinksInTextAttachment,
  addBackgroundToTransparentImages,
  handleLinkClickHistory,
  addBrightnessSlider,
  addBlurSlider,
  disableFuzzyLinks,
  escapeHtml,
  innerStyle,
}) => {
  const [brightness, setBrightness] = useState(addBrightnessSlider ? 0 : 1);
  const [blur, setBlur] = useState(addBlurSlider ? 0 : 1);
  const Markdown = escapeHtml ? BulbaMarkdown : DefaultMarkdown;

  if (disableFuzzyLinks) {
    Markdown.linkify.set({ fuzzyLink: false });
  }

  // sad hack for sc819479 because the latex parsing package is dumb and bugs out when there's a period after double dollar signs
  // another sad hack for sc822871 because it bugs out when there are spaces before/after dollar sign tags
  const replacedText = text.replaceAll('$$.', '.$$$$').replaceAll(/ \$ +(.*?) +\$/g, ' $$$1$$');
  let textContent;

  if (replacedText.includes(MARKDOWN_DIVIDER)) {
    const sections = parseMarkdownSections(replacedText);
    textContent = sections
      .map(section => {
        return `<div>${Markdown.render(section.content)}</div>`;
      })
      .join('');
  } else {
    textContent = Markdown.render(replacedText);
  }
  // link disabling logic
  if (disableLinksInTextAttachment) {
    // For certain sensitive projects, we want to disable any link.
    // This block remove any HTML <a> tags and render the text as is.
    textContent = sanitizeHtml(textContent, {
      allowedTags: sanitizeHtml.defaults.allowedTags.filter((o: string) => o !== 'a'),
      disallowedTagsMode: 'discard',
    });
  }

  const classes = useStyles();

  const handleClick = (e: any) =>
    e.type === 'click' &&
    e.target?.href &&
    handleLinkClickHistory &&
    handleLinkClickHistory(e.target.href);

  return (
    <div>
      <link
        rel="stylesheet"
        href="https://cdn.jsdelivr.net/gh/highlightjs/cdn-release@11.7.0/build/styles/default.min.css"
      />
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.13.18/dist/katex.min.css" />

      <HorizontalBrightnessBlurSliders
        addBrightnessSlider={addBrightnessSlider}
        addBlurSlider={addBlurSlider}
        addZoomSlider={false}
        setBrightness={setBrightness}
        setBlur={setBlur}
        brightness={brightness}
        blur={blur}
      />
      <div
        style={{
          filter: `brightness(${brightness * 100}%) blur(${(1 - blur) * 50}px)`,
          ...(typeof innerStyle === 'string' ? {} : innerStyle),
        }}
        dangerouslySetInnerHTML={{
          __html:
            innerStyle && typeof innerStyle === 'string'
              ? textContent.replaceAll('<p>', `<p style="${innerStyle}">`)
              : textContent.replaceAll('<p>', '<p style="margin-bottom: 24px">'),
        }}
        className={cx({
          [classes.inlineBlockCode]: true,
          [classes.imageOverride]: addBackgroundToTransparentImages,
        })}
        onClick={e => handleClick(e)}
      />
    </div>
  );
};

export default SanitizedMarkdown;
